import FormSection from '@/components/Forms/FormSection';
import { Box } from '@palmetto/palmetto-components';
import { Link } from 'react-router-dom';

import {
  GeneratorInstallPackageFileCategory,
  InstallationPackagePhotoCategory,
  ProgramType,
  RequirementFlagSettingsType,
} from 'types';

import { PhotosList } from '../Documents/PhotosList';

export const SystemPhotos = ({
  showCheckboxes,
  checkboxCategories,
  isPackageEditable = true,
  isFinCoUser,
}: {
  showCheckboxes: boolean;
  checkboxCategories: GeneratorInstallPackageFileCategory[];
  isPackageEditable?: boolean;
  isFinCoUser: boolean;
}) => {
  return (
    <>
      <FormSection
        title="New System Photos"
        description={
          <>
            <Box>Upload photos to all applicable categories.</Box>
            <Box>
              Reference <Link to={''}>Install Photo Requirements documentation</Link> for specifics on submission
              requirements.
            </Box>
          </>
        }
      >
        <PhotosList
          title="Equipment Data Plate"
          description="Photo must show manufacturer's data plate with serial number, model number, and kW rating clearly visible"
          category={InstallationPackagePhotoCategory.equipmentDataPlate}
          baseCategory="Installation Photo Documentation"
          flagType={RequirementFlagSettingsType.equipmentDataPlateGenerator}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          programType={ProgramType.generator}
          isPackageEditable={isPackageEditable}
        />
        <PhotosList
          title="Wide Installation View"
          description="Photo must show the complete generator unit, pad, and surrounding installation area with required clearances visible"
          category={InstallationPackagePhotoCategory.wideInstallationView}
          baseCategory="Installation Photo Documentation"
          flagType={RequirementFlagSettingsType.wideInstallationViewGenerator}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          programType={ProgramType.generator}
          isPackageEditable={isPackageEditable}
        />
        <PhotosList
          title="Transfer Switch / ATS"
          description="Photo must show the installed Automatic Transfer Switch and connection to the home's electrical panel"
          category={InstallationPackagePhotoCategory.transferSwitch}
          baseCategory="Installation Photo Documentation"
          flagType={RequirementFlagSettingsType.transferSwitchGenerator}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          programType={ProgramType.generator}
          isPackageEditable={isPackageEditable}
        />
        <PhotosList
          title="Fuel Connection"
          description="Photo must show the fuel line connection (Natural Gas or LP) including shutoff valve and regulator (where applicable)"
          category={InstallationPackagePhotoCategory.fuelConnection}
          baseCategory="Installation Photo Documentation"
          flagType={RequirementFlagSettingsType.fuelConnectionGenerator}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          programType={ProgramType.generator}
          isPackageEditable={isPackageEditable}
          showIfEmpty={isFinCoUser}
        />
        <PhotosList
          title="Remote Monitoring Setup"
          description="Photo or screenshot must show Mobile Link (or equivalent) confirmation that the unit is online and reporting"
          category={InstallationPackagePhotoCategory.remoteMonitoringSetup}
          baseCategory="Installation Photo Documentation"
          flagType={RequirementFlagSettingsType.remoteMonitoringSetupGenerator}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          programType={ProgramType.generator}
          isPackageEditable={isPackageEditable}
        />
        <PhotosList
          title="Cellular Device ID"
          description="Provide a high-resolution photo of the cellular device ID label, clear enough to allow Mobile Link service to be enabled later"
          category={InstallationPackagePhotoCategory.cellularDeviceId}
          baseCategory="Installation Photo Documentation"
          flagType={RequirementFlagSettingsType.cellularDeviceIdGenerator}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          programType={ProgramType.generator}
          isPackageEditable={isPackageEditable}
          showIfEmpty={isFinCoUser}
        />
        <PhotosList
          title="Main Service Panel"
          description="Provide a photo of the main panel with the door open, showing backed-up loads and overall panel condition"
          category={InstallationPackagePhotoCategory.mainServicePanel}
          baseCategory="Installation Photo Documentation"
          flagType={RequirementFlagSettingsType.mainServicePanelGenerator}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          programType={ProgramType.generator}
          isPackageEditable={isPackageEditable}
          showIfEmpty={isFinCoUser}
        />
        <PhotosList
          title="Load Control Devices"
          description="Provide a single pullback photo showing all installed load control / smart management modules"
          category={InstallationPackagePhotoCategory.loadControlDevices}
          baseCategory="Installation Photo Documentation"
          flagType={RequirementFlagSettingsType.loadControlDevicesGenerator}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          programType={ProgramType.generator}
          isPackageEditable={isPackageEditable}
          showIfEmpty={isFinCoUser}
        />
        <PhotosList
          title="FieldPro Screenshots: Fuel Line Pressure Test"
          description="Provide three sequential FieldPro app screenshots: immediately after fuel line install showing constant pressure, generator running test, and under-load test. All three should show a passing pressure reading"
          category={InstallationPackagePhotoCategory.fieldProFuelLinePressure}
          baseCategory="Installation Photo Documentation"
          flagType={RequirementFlagSettingsType.fieldProFuelLinePressureGenerator}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          programType={ProgramType.generator}
          isPackageEditable={isPackageEditable}
          showIfEmpty={isFinCoUser}
        />
        <PhotosList
          title="FieldPro Screenshots: System Runtime"
          description="Provide a single screenshot showing all parameters green with no active alarms, captured after a minimum 30-minute (up to 1 hour) run"
          category={InstallationPackagePhotoCategory.fieldProSystemRuntime}
          baseCategory="Installation Photo Documentation"
          flagType={RequirementFlagSettingsType.fieldProSystemRuntimeGenerator}
          showCheckboxes={showCheckboxes}
          checkboxCategories={checkboxCategories}
          programType={ProgramType.generator}
          isPackageEditable={isPackageEditable}
          showIfEmpty={isFinCoUser}
        />
      </FormSection>
    </>
  );
};
